import { LinkTypes } from "entity-app/constants";
import { getImplicitMaterialDisplayTitle } from "entity-app/shared-components/entity-dateblock/utils/implicit-material-title";
import {
  SiteActivityLink,
  SiteMaterialLink,
  SiteSubmittalLink
} from "pages/design-package-details-page/components/view-dependencies/model";

interface RawLink<T> {
  id?: string;
  link_type?: number | null;
  target_feature_instance?: T | null;
}

interface RawActivity {
  id?: string;
  title?: string | null;
  feature_activities?: Array<{
    start_date?: string | null;
    end_date?: string | null;
  }> | null;
}

interface RawMaterial {
  id?: string;
  title?: string | null;
  implicit?: boolean | null;
  linked_activities?: RawLink<RawActivity>[] | null;
}

interface RawSubmittal {
  id?: string;
  title?: string | null;
  auto_inc_value?: string | number | null;
  linked_activities?: RawLink<RawActivity>[] | null;
  submittal_materials?: RawLink<RawMaterial>[] | null;
}

interface RawSubmittalPlan {
  id?: string;
  bid_package_materials?: RawLink<RawMaterial>[] | null;
  material_plans?:
    | RawLink<{
        id?: string;
        linked_activities?: RawLink<RawActivity>[] | null;
      }>[]
    | null;
}

/** Response row of QUERY_BID_PACKAGE_LINKED_ENTITIES. */
export interface RawLinkedBidPackage {
  id?: string;
  bid_package_submittals?: RawLink<RawSubmittal>[] | null;
  submittal_plans?: RawLink<RawSubmittalPlan>[] | null;
}

export type ActivityDateLabel = "Start Date" | "End Date";

/** Linked activity, labelled with which of its dates the link uses. */
export interface LinkedActivity extends SiteActivityLink {
  dateLabel: ActivityDateLabel | null;
}

export interface LinkedEntities {
  activities: LinkedActivity[];
  materials: SiteMaterialLink[];
  submittals: SiteSubmittalLink[];
}

function targetsOf<T extends { id?: string }>(
  links: RawLink<T>[] | null | undefined
): Array<T & { id: string }> {
  return (links ?? [])
    .map((link) => link.target_feature_instance)
    .filter((target): target is T & { id: string } => !!target?.id);
}

function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const byId = new Map<string, T>();
  items.forEach((item) => {
    if (!byId.has(item.id)) byId.set(item.id, item);
  });
  return [...byId.values()];
}

/**
 * Start date for a finish-to-start link, else the end date; falls back to
 * whichever the activity has. Labelled with the date actually used.
 */
function linkedActivityDate(link: RawLink<RawActivity>): {
  date: string | null;
  dateLabel: ActivityDateLabel | null;
} {
  const activity = link.target_feature_instance?.feature_activities?.[0];
  const start = activity?.start_date ?? null;
  const end = activity?.end_date ?? null;
  const startFirst =
    (link.link_type ?? LinkTypes.finish_to_start) ===
    (LinkTypes.finish_to_start as number);
  if (start && (startFirst || !end)) {
    return { date: start, dateLabel: "Start Date" };
  }
  if (end) return { date: end, dateLabel: "End Date" };
  return { date: null, dateLabel: null };
}

function activityDate(link: RawLink<RawActivity>): string | null {
  return linkedActivityDate(link).date;
}

/** Links whose target is a schedule activity (has feature_activities). */
function activitiesOf(
  links: RawLink<RawActivity>[] | null | undefined
): SiteMaterialLink["activities"] {
  return uniqueById(
    (links ?? [])
      .filter(
        (link) =>
          link.target_feature_instance?.id &&
          link.target_feature_instance.feature_activities?.length
      )
      .map((link) => ({
        id: link.target_feature_instance?.id ?? "",
        name: link.target_feature_instance?.title?.trim() ?? "",
        date: activityDate(link)
      }))
  );
}

/** Which date ("Start Date"/"End Date") the link to this activity uses. */
function activityDateLabel(
  links: RawLink<RawActivity>[],
  id: string
): ActivityDateLabel | null {
  const link = links.find((item) => item.target_feature_instance?.id === id);
  return link ? linkedActivityDate(link).dateLabel : null;
}

function earliest(dates: Array<string | null>): string | null {
  const known = dates.filter((date): date is string => !!date);
  if (known.length === 0) return null;
  return known.sort((a, b) => Date.parse(a) - Date.parse(b))[0];
}

/**
 * A submittal linked to several materials hides its implicit material; it is
 * only listed when it is the submittal's sole material.
 */
function withoutImplicitWhenMultiple<T extends RawMaterial>(
  materials: T[]
): T[] {
  if (materials.length <= 1) return materials;
  return materials.filter((material) => !material.implicit);
}

/**
 * Activities linked to the submittal itself: its own activity links, plus
 * those reached through an implicit material that is not listed.
 */
function submittalActivities(
  submittal: RawSubmittal,
  hiddenMaterials: RawMaterial[]
): SiteMaterialLink["activities"] {
  return uniqueById([
    ...activitiesOf(submittal.linked_activities),
    ...hiddenMaterials.flatMap((material) =>
      activitiesOf(material.linked_activities)
    )
  ]);
}

/**
 * Entities linked to the bid package. An implicit material is listed under
 * its submittal as "Material for Submittal {auto_inc_value}".
 */
export function resolveLinkedEntities(
  bp: RawLinkedBidPackage | null | undefined
): LinkedEntities {
  const submittals = uniqueById(targetsOf(bp?.bid_package_submittals)).map(
    (submittal) => {
      const allMaterials = uniqueById(targetsOf(submittal.submittal_materials));
      const shown = withoutImplicitWhenMultiple(allMaterials);
      const hidden = allMaterials.filter(
        (material) => !shown.includes(material)
      );
      const activities = submittalActivities(submittal, hidden);
      return {
        id: submittal.id,
        title: submittal.title?.trim() ?? "",
        ...(activities.length ? { activities } : {}),
        materials: shown.map((material) => {
          const materialActivities = activitiesOf(material.linked_activities);
          return {
            id: material.id,
            title: material.implicit
              ? getImplicitMaterialDisplayTitle(submittal.auto_inc_value)
              : (material.title?.trim() ?? ""),
            date: earliest(materialActivities.map((activity) => activity.date)),
            activities: materialActivities
          };
        })
      };
    }
  );

  const submittalPlans = targetsOf(bp?.submittal_plans);

  const materials = uniqueById(
    submittalPlans.flatMap((plan) => targetsOf(plan.bid_package_materials))
  ).map((material) => ({
    id: material.id,
    title: material.title?.trim() ?? "",
    activities: activitiesOf(material.linked_activities)
  }));

  const activityLinks = submittalPlans
    .flatMap((plan) => targetsOf(plan.material_plans))
    .flatMap((materialPlan) => materialPlan.linked_activities ?? []);
  const activities = uniqueById(
    submittalPlans
      .flatMap((plan) => targetsOf(plan.material_plans))
      .flatMap((materialPlan) => activitiesOf(materialPlan.linked_activities))
  ).map((activity) => ({
    ...activity,
    dateLabel: activityDateLabel(activityLinks, activity.id),
    materialName: null
  }));

  return { activities, materials, submittals };
}
